import { engravePhrase, tieContinuationTicks } from './engrave.ts';
import { beatMs, msPerTick } from './meter.ts';
import { expectedTapTicks, phraseMeter, type RhythmPhrase } from './phrase.ts';

/**
 * Tap grading. Taps are milliseconds from the first beat of the phrase (after
 * the count-in), already corrected for this device's audio/input latency.
 * Each written sound's start is paired with the nearest unused tap inside a
 * tempo-scaled window; unmatched taps are "extra" and are explained by what
 * the music has there (a rest, a tied note, or nothing).
 */

export type TapStatus = 'on-time' | 'early' | 'late' | 'missed';
export type ExtraTapReason = 'rest' | 'tie' | 'extra';

export interface NoteResult {
  tick: number;
  expectedMs: number;
  status: TapStatus;
  /** Tap minus expected time (negative = early). Null when missed. */
  offsetMs: number | null;
}

export interface ExtraTap {
  ms: number;
  reason: ExtraTapReason;
}

export interface RhythmGrade {
  notes: NoteResult[];
  extras: ExtraTap[];
  onTime: number;
  early: number;
  late: number;
  missed: number;
  /** Mean signed offset of matched taps (negative = rushing). Null with no matches. */
  meanOffsetMs: number | null;
  /** Share of notes tapped on time (0–1). */
  accuracy: number;
  /** Counts as a correct performance: nothing missed, no extra taps, and at most 20% early/late (always at least one note). */
  correct: boolean;
  windows: { onTimeMs: number; matchMs: number };
}

export const RHYTHM_GRADING = {
  /** On-time window as a share of one beat, clamped to [min, max] ms. */
  onTimeShare: 0.08,
  onTimeMinMs: 35,
  onTimeMaxMs: 70,
  /** A tap further than this from any note is not an attempt at it. */
  matchShare: 0.22,
  matchMinMs: 80,
  matchMaxMs: 180,
  /** Share of notes that may be early/late in a correct performance. */
  maxOffShare: 0.2,
  /** …but never fewer than this many, so one slightly early or late note in a short rhythm still passes. */
  minOffAllowed: 1,
} as const;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function gradingWindows(phrase: RhythmPhrase, bpm: number): { onTimeMs: number; matchMs: number } {
  const ts = phraseMeter(phrase);
  const beat = beatMs(bpm);
  const onTimeMs = clamp(beat * RHYTHM_GRADING.onTimeShare, RHYTHM_GRADING.onTimeMinMs, RHYTHM_GRADING.onTimeMaxMs);
  // Never wider than half the gap between neighbouring notes, so a tap can't be claimed by the wrong note.
  const ticks = expectedTapTicks(phrase);
  const tickMs = msPerTick(ts, bpm);
  let minGap = Infinity;
  for (let i = 1; i < ticks.length; i++) minGap = Math.min(minGap, (ticks[i]! - ticks[i - 1]!) * tickMs);
  const matchMs = Math.max(onTimeMs + 5, Math.min(clamp(beat * RHYTHM_GRADING.matchShare, RHYTHM_GRADING.matchMinMs, RHYTHM_GRADING.matchMaxMs), minGap / 2));
  return { onTimeMs, matchMs };
}

export function gradeTaps(phrase: RhythmPhrase, bpm: number, tapsMs: readonly number[]): RhythmGrade {
  const ts = phraseMeter(phrase);
  const tickMs = msPerTick(ts, bpm);
  const windows = gradingWindows(phrase, bpm);
  const expected = expectedTapTicks(phrase).map((tick) => ({ tick, ms: tick * tickMs }));
  const taps = [...tapsMs].filter((t) => Number.isFinite(t)).sort((a, b) => a - b);

  // Pair closest first, each note and each tap used once.
  const pairs: { note: number; tap: number; distance: number }[] = [];
  expected.forEach((e, note) =>
    taps.forEach((t, tap) => {
      const distance = Math.abs(t - e.ms);
      if (distance <= windows.matchMs) pairs.push({ note, tap, distance });
    }),
  );
  pairs.sort((a, b) => a.distance - b.distance || a.note - b.note);
  const noteTap = new Map<number, number>();
  const usedTaps = new Set<number>();
  for (const p of pairs) {
    if (noteTap.has(p.note) || usedTaps.has(p.tap)) continue;
    noteTap.set(p.note, p.tap);
    usedTaps.add(p.tap);
  }

  const notes: NoteResult[] = expected.map((e, i) => {
    const tapIndex = noteTap.get(i);
    if (tapIndex === undefined) return { tick: e.tick, expectedMs: e.ms, status: 'missed', offsetMs: null };
    const offset = taps[tapIndex]! - e.ms;
    const status: TapStatus = Math.abs(offset) <= windows.onTimeMs ? 'on-time' : offset < 0 ? 'early' : 'late';
    return { tick: e.tick, expectedMs: e.ms, status, offsetMs: Math.round(offset) };
  });

  const ties = tieContinuationTicks(engravePhrase(phrase)).map((tick) => tick * tickMs);
  const extras: ExtraTap[] = taps
    .filter((_, i) => !usedTaps.has(i))
    .map((ms) => {
      if (ties.some((t) => Math.abs(ms - t) <= windows.matchMs)) return { ms, reason: 'tie' as const };
      const tick = ms / tickMs;
      const inRest = phrase.events.some((e) => e.rest && tick >= e.start - windows.matchMs / tickMs && tick < e.start + e.duration);
      return { ms, reason: inRest ? ('rest' as const) : ('extra' as const) };
    });

  const count = (s: TapStatus) => notes.filter((n) => n.status === s).length;
  const onTime = count('on-time');
  const early = count('early');
  const late = count('late');
  const missed = count('missed');
  const offsets = notes.flatMap((n) => (n.offsetMs === null ? [] : [n.offsetMs]));
  const meanOffsetMs = offsets.length ? Math.round(offsets.reduce((a, b) => a + b, 0) / offsets.length) : null;
  const correct = missed === 0 && extras.length === 0 && early + late <= Math.max(RHYTHM_GRADING.minOffAllowed, Math.floor(notes.length * RHYTHM_GRADING.maxOffShare));
  return { notes, extras, onTime, early, late, missed, meanOffsetMs, accuracy: notes.length ? onTime / notes.length : 0, correct, windows };
}

export interface RhythmFeedback {
  title: string;
  body: string;
}

/** Plain-language feedback, most useful point first. */
export function rhythmFeedback(grade: RhythmGrade): RhythmFeedback {
  const total = grade.notes.length;
  const restTaps = grade.extras.filter((x) => x.reason === 'rest').length;
  const tieTaps = grade.extras.filter((x) => x.reason === 'tie').length;
  const otherExtras = grade.extras.length - restTaps - tieTaps;
  const tips: string[] = [];
  if (tieTaps) tips.push(`Tied notes are held, not tapped again — you tapped ${tieTaps === 1 ? 'a tied note' : `${tieTaps} tied notes`}.`);
  if (restTaps) tips.push(`You tapped during ${restTaps === 1 ? 'a rest' : `${restTaps} rests`}. Rests are silent: keep counting, but don’t tap.`);
  if (grade.missed) tips.push(`${grade.missed === 1 ? 'One note was' : `${grade.missed} notes were`} missed.`);
  if (otherExtras) tips.push(`${otherExtras === 1 ? 'One extra tap' : `${otherExtras} extra taps`} didn’t match a note.`);
  const off = grade.early + grade.late;
  if (off && grade.meanOffsetMs !== null && Math.abs(grade.meanOffsetMs) >= grade.windows.onTimeMs / 2) {
    tips.push(grade.meanOffsetMs < 0 ? 'You tended to rush — tap a little later, right with the beat.' : 'You tended to drag — tap a little sooner, right with the beat.');
  } else if (off) {
    tips.push(`${off} of ${total} notes were a little early or late.`);
  }
  if (grade.correct && grade.onTime === total) return { title: 'Right in time!', body: 'Every note on the beat. Try it faster, or move on to the next phrase.' };
  if (grade.correct) return { title: 'Nicely done!', body: tips.join(' ') || 'Steady and accurate.' };
  return { title: grade.missed + grade.extras.length > total / 2 ? 'Let’s try that again' : 'Almost there', body: tips.join(' ') };
}

/**
 * One machine-readable classification per performance, used for teacher error
 * patterns. Most instructive problem first (the same order as the feedback):
 * tapping a tied note or a rest, a missed note, a stray tap, then timing that
 * was too often early (rushing) or late (dragging). Mirrored in SQL
 * (`app_private.grade_rhythm`) and parity-tested.
 */
export type RhythmGradeCode = 'correct' | 'tapped-tie' | 'tapped-rest' | 'missed-note' | 'extra-tap' | 'rushing' | 'dragging' | 'uneven';

export const RHYTHM_GRADE_CODE_LABEL: Record<RhythmGradeCode, string> = {
  correct: 'Correct',
  'tapped-tie': 'Tapped a tied note',
  'tapped-rest': 'Tapped during a rest',
  'missed-note': 'Missed a note',
  'extra-tap': 'Extra tap',
  rushing: 'Rushing',
  dragging: 'Dragging',
  uneven: 'Uneven timing',
};

export function rhythmGradeCode(grade: RhythmGrade): RhythmGradeCode {
  if (grade.correct) return 'correct';
  if (grade.extras.some((x) => x.reason === 'tie')) return 'tapped-tie';
  if (grade.extras.some((x) => x.reason === 'rest')) return 'tapped-rest';
  if (grade.missed > 0) return 'missed-note';
  if (grade.extras.length > 0) return 'extra-tap';
  if (grade.early > grade.late) return 'rushing';
  if (grade.late > grade.early) return 'dragging';
  return 'uneven';
}

/** Short summary stored with each answer, e.g. "5/6 on time". */
export function rhythmAnswerText(grade: RhythmGrade): string {
  return `${grade.onTime}/${grade.notes.length} on time`;
}
